import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ValidateCoredataUrl } from 'app/helpers/validators';
import { CoredataDocumentRef } from 'app/interfaces/coredataDocumentRef';
import { ExposedCreateSpousePensionApplicationOtherFundsReq } from 'app/interfaces/requests/Exposed/exposedCreateSpousePensionApplicationOtherFundsReq';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadCoredataFileSenderRes } from 'app/interfaces/responses/Exposed/exposedReadCoredataFileSenderRes';
import { ExposedSubmitOtherFundsApplicationRes } from 'app/interfaces/responses/Exposed/exposedSubmitOtherFundsApplicationRes';
import { OtherFundsApplicationsService } from 'app/services/api/applications/other-funds-applications.service';
import { PublicRegistrySearchComponent } from 'common/components/public-registry-search/public-registry-search.component';

@Component({
    selector: 'app-pension-spouse-form',
    templateUrl: './pension-spouse-form.component.html',
    styleUrls: ['./pension-spouse-form.component.scss'],
})
export class PensionSpouseFormComponent implements OnInit {
    readonly applicationTypeKey = 'SpousePensionApplication_OF';

    @ViewChild('applicantSearch') applicantSearch: PublicRegistrySearchComponent;
    @ViewChild('deceasedSearch') deceasedSearch: PublicRegistrySearchComponent;

    @Output() submitted = new EventEmitter<ExposedSubmitOtherFundsApplicationRes>();
    @Output() inputStarted = new EventEmitter<void>();

    form: UntypedFormGroup;

    applicantSelectionError = false;
    deceasedSelectionError = false;
    signedDocError = false;
    additionalDocError = false;
    submitting = false;
    lookupInProgress = false;

    constructor(
        private otherFundsApplicationsService: OtherFundsApplicationsService,
        private formBuilder: UntypedFormBuilder
    ) {}

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            applicantSSN: new UntypedFormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
            deceasedSSN: new UntypedFormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
            signedDocUrl: new UntypedFormControl('', [Validators.required, ValidateCoredataUrl()]),
            signedDocName: new UntypedFormControl('', [Validators.required]),
            additionalDocs: this.formBuilder.array([]),
        });
    }

    get additionalDocs(): UntypedFormArray {
        return this.form.get('additionalDocs') as UntypedFormArray;
    }

    addAdditionalDoc(): void {
        this.additionalDocs.push(
            this.formBuilder.group({
                url: new UntypedFormControl('', [Validators.required, ValidateCoredataUrl()]),
                name: new UntypedFormControl('', [Validators.required]),
            })
        );
    }

    removeAdditionalDoc(index: number): void {
        this.additionalDocs.removeAt(index);
    }

    onApplicantSelected(person: ExposedListPublicRegistryPersonsRes | null): void {
        this.inputStarted.emit();
        this.form.get('applicantSSN')?.setValue(person?.ssn ?? '');
        this.applicantSelectionError = false;
    }

    onDeceasedSelected(person: ExposedListPublicRegistryPersonsRes | null): void {
        this.inputStarted.emit();
        this.form.get('deceasedSSN')?.setValue(person?.ssn ?? '');
        this.deceasedSelectionError = false;
    }

    onSignedDocChange(): void {
        this.signedDocError = false;
        this.additionalDocError = false;
    }

    onAdditionalDocChange(): void {
        this.additionalDocError = false;
    }

    private hasDuplicateUrls(): boolean {
        const urls: string[] = [];
        const signedUrl = this.form.get('signedDocUrl')?.value?.trim();
        if (signedUrl) {
            urls.push(signedUrl.toLowerCase());
        }
        for (const doc of this.additionalDocs.controls) {
            const url = doc.get('url')?.value?.trim();
            if (url) {
                urls.push(url.toLowerCase());
            }
        }
        const uniqueUrls = new Set(urls);
        return uniqueUrls.size !== urls.length;
    }

    submit(): void {
        this.form.markAllAsTouched();

        if (!this.form.get('applicantSSN')?.value) {
            this.applicantSelectionError = true;
        }

        if (!this.form.get('deceasedSSN')?.value) {
            this.deceasedSelectionError = true;
        }

        if (this.form.invalid || this.applicantSelectionError || this.deceasedSelectionError) {
            return;
        }

        const signedDoc: CoredataDocumentRef = {
            url: this.form.get('signedDocUrl')?.value,
            fileName: this.form.get('signedDocName')?.value,
        };
        if (!signedDoc.url || !signedDoc.fileName) {
            this.signedDocError = true;
            return;
        }

        if (this.hasDuplicateUrls()) {
            this.additionalDocError = true;
            return;
        }

        const additionalDocs: CoredataDocumentRef[] = this.additionalDocs.controls.map((group) => ({
            url: group.get('url')?.value,
            fileName: group.get('name')?.value,
        }));

        const payload: ExposedCreateSpousePensionApplicationOtherFundsReq = {
            applicantSSN: this.form.get('applicantSSN')?.value,
            deceasedSSN: this.form.get('deceasedSSN')?.value,
            signedApplicationDocument: signedDoc,
            additionalDocuments: additionalDocs.length > 0 ? additionalDocs : undefined,
        };

        this.submitting = true;
        this.otherFundsApplicationsService
            .submitPensionOtherFundsApplication(this.applicationTypeKey, payload)
            .subscribe((result) => {
                this.submitted.emit(result);
                this.resetForm();
            })
            .add(() => {
                this.submitting = false;
            });
    }

    signedDocumentFileNameResolver = (fileInfo: ExposedReadCoredataFileSenderRes): string => {
        const fileName = fileInfo.fileName?.trim();

        const sender = this.getSenderFromFileName(fileName) || '<Heiti lífeyrissjóðs>';
        const applicantSSN = (this.form.get('applicantSSN')?.value ?? '').trim() || '<Kennitala umsækjanda>';

        return `${sender} - M umsókn - ${applicantSSN}`;
    };

    private getSenderFromFileName(fileName?: string): string | undefined {
        if (!fileName || fileName.trim().length === 0) {
            return undefined;
        }

        const index = fileName.indexOf('-');

        if (index <= 0) {
            return fileName.trim();
        }

        return fileName.substring(0, index).trim();
    }

    private resetForm(): void {
        this.form.reset({
            applicantSSN: '',
            deceasedSSN: '',
            signedDocUrl: '',
            signedDocName: '',
        });
        this.applicantSearch?.clearSelection();
        this.deceasedSearch?.clearSelection();
        this.applicantSelectionError = false;
        this.deceasedSelectionError = false;
        this.signedDocError = false;
        this.additionalDocError = false;
        while (this.additionalDocs.length) {
            this.additionalDocs.removeAt(0);
        }
    }
}
